import { useState, useEffect } from 'react';
import { OKCancel, PlusInput, LoadingIndicator } from '../../lib/iprs-react-library/src/index.js';
import useRotaTypeToggle from '../Team/useRotaTypeToggle';
import { loadTeamGroups } from '../Team/teamGroups';
import userLoggedInHasRole from '../Util/userLoggedInHasRole';
import Notice from '../Util/Notice';

const NO_GROUPS = [];

const RotaTypeGroupSettings = ({ state, eventDispatch }) => {

    const { api } = state;


    const groups = state.team?.Groups ?? NO_GROUPS;

    const hasDiaryManagerPerm = userLoggedInHasRole(api, 'Diary Manager');

    const [selectedGroupID, setSelectedGroupID] = useState(null);
    const [creatingRotaType, setCreatingRotaType] = useState(null); // { Description, Colour } or null
    const [notice, setNotice] = useState(null); // { text, isError }, cleared by the next action

    const { rotaTypes, setRotaTypes, savingRotaTypeID, isIncluded, toggleTask } = useRotaTypeToggle(api, setNotice);

    // Refresh the shared list once when Settings opens, in case it's stale
    useEffect(() => {
        loadTeamGroups(api, eventDispatch)
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to load team groups', isError: true }));
    }, [api, eventDispatch]);

    // Default to the first team group once the list has loaded, and pick
    useEffect(() => {
        const selectedStillExists = groups.some(g => g.GroupID === selectedGroupID);
        if (!selectedStillExists) {
            setSelectedGroupID(groups.length ? groups[0].GroupID : null);
        }
    }, [groups, selectedGroupID]);

    const selectedGroup = groups.find(g => g.GroupID === selectedGroupID);

    const onSelectGroup = e => {
        setNotice(null);
        setSelectedGroupID(Number.parseInt(e.target.value, 10));
    };

    const onCreateClick = e => {
        e.preventDefault();
        setNotice(null);
        setCreatingRotaType({ Description: e.target.value || '', Colour: '#00FF00' });
    };

    const onCancelCreate = () => {
        setNotice(null);
        setCreatingRotaType(null);
    };

    const onSaveNewRotaType = () => {
        const description = (creatingRotaType.Description || '').trim();
        if (!description) {
            setNotice({ text: 'Please enter a description for the task type', isError: true });
            return;
        }

        setNotice(null);
        api.transact('RotaTypeCreate', { ...creatingRotaType, Description: description })
            .then(r => {
                setRotaTypes(r.apiResult.RotaTypes || []);
                setCreatingRotaType(null);
                setNotice({ text: `"${description}" created`, isError: false });
            })
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to create task type', isError: true }));
    };

    return (
        <div>
            {/* While the create overlay is open its errors are shown inside it */}
            {!creatingRotaType && <Notice notice={notice} onDismiss={() => setNotice(null)} />}

            <section className='settings-section'>
                <h3>Team Task Types</h3>
                <p>Every task type is available to every team group by default.
                    Pick a team group, then include or exclude its task types.</p>

                {!groups.length && <p>No team groups found.</p>}

                {!!groups.length &&
                    <ul className='settings-list'>
                        <li>
                            <span className='settings-list__label'><strong>Team group</strong></span>
                            <select value={selectedGroupID ?? ''} onChange={onSelectGroup} >
                                {groups.map(g => <option key={g.GroupID} value={g.GroupID}>{g.GroupTitle}</option>)}
                            </select>
                        </li>
                    </ul>
                }

                {selectedGroup &&
                    <>
                        <h4>{selectedGroup.GroupTitle} task types</h4>
                        <div className='settings-list-scroll'>
                            <ul className='settings-list'>
                                {rotaTypes.map(rt => {
                                    const included = isIncluded(rt, selectedGroupID);
                                    const saving = savingRotaTypeID === rt.RotaTypeID;
                                    return (
                                        <li key={rt.RotaTypeID}>
                                            <span className='settings-list__label'>{rt.Description}</span>
                                            {saving && <LoadingIndicator loading={true} size={16} />}
                                            <button type='button'
                                                className={'settings-table__toggle ' +
                                                    (included ? 'settings-table__toggle--included' : '')}
                                                disabled={!hasDiaryManagerPerm || saving}
                                                title={hasDiaryManagerPerm
                                                    ? `Click to ${included ? 'exclude from' : 'include for'} ${selectedGroup.GroupTitle}`
                                                    : undefined}
                                                onClick={() => toggleTask(rt, selectedGroupID)} >
                                                {included ? 'Included' : 'Excluded'}
                                            </button>
                                        </li>
                                    );
                                })}
                                {0 === rotaTypes.length &&
                                    <li><span className='settings-list__label'>No task types found.</span></li>
                                }
                            </ul>
                        </div>
                    </>
                }

                {hasDiaryManagerPerm && <PlusInput onClick={onCreateClick} />}
            </section>

            {creatingRotaType && (
                <>
                    <div className='settings-overlay-backdrop' onClick={onCancelCreate} />
                    <section className='settings-overlay'>
                        <Notice notice={notice} onDismiss={() => setNotice(null)} />
                        <form>
                            <label htmlFor='Description'>Description</label>
                            <input type='text' name='Description' value={creatingRotaType.Description}
                                onChange={e => setCreatingRotaType({ ...creatingRotaType, Description: e.target.value })}
                                size='40' />

                            <label htmlFor='Colour'>Colour</label>
                            <input type='color' name='Colour' value={creatingRotaType.Colour}
                                onChange={e => setCreatingRotaType({ ...creatingRotaType, Colour: e.target.value })} />

                            <OKCancel onOK={onSaveNewRotaType} onCancel={onCancelCreate} />
                        </form>
                    </section>
                </>
            )}
        </div>
    );
};

export default RotaTypeGroupSettings;
